/**
 * Pure calculations for the six free tools. Shared by the browser (live result
 * and report) and the lead route (which rebuilds the report from the inputs so
 * emailed content never comes from the client).
 */
import { z } from "zod";

export type Tone = "red" | "amber" | "green";
export type StatIcon = "trend" | "low" | "calendar" | "percent" | "clock" | "users" | "list" | "check" | "alert";
export type ToolSlug =
  | "cash-runway-calculator"
  | "price-change-impact-calculator"
  | "time-and-accountability-grid"
  | "fractional-coo-cost-calculator"
  | "revenue-leakage-check"
  | "weekly-scorecard-builder"
  | "partner-alignment-check";

export type ReportTable = {
  caption: string;
  headers: string[];
  rows: string[][];
  /** Row indexes to highlight (chosen or lowest). */
  highlight?: number[];
  /** Row indexes to show in the alert colour. */
  negative?: number[];
};

export type ReportModel = {
  slug: ToolSlug;
  toolName: string;
  headline: string;
  headlineSub?: string;
  stats: { icon: StatIcon; label: string; value: string }[];
  tone: Tone;
  verdict: string;
  table: ReportTable;
  actions: string[];
  serviceName: string;
  serviceText: string;
  /** Short numeric summary stored with the lead. */
  summary: Record<string, string | number | null>;
  disclaimer?: string;
};

const aud0 = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 });
export const aud = (n: number) => aud0.format(Math.round(n));
export const signedAud = (n: number) => (n > 0 ? `+${aud(n)}` : aud(n));
export const pct = (n: number, dp = 1) => `${(Math.round(n * 10 ** (dp + 2)) / 10 ** dp).toFixed(dp).replace(/\.0$/, "")}%`;
const num = (min: number, max: number) => z.coerce.number().finite().min(min).max(max);

/* ------------------------------------------------------------------ */
/* Tool 1: Cash runway                                                 */
/* ------------------------------------------------------------------ */
export const cashInputSchema = z.object({
  cash: num(-1e9, 1e10),
  monthlyIn: num(0, 1e10),
  monthlyOut: num(0, 1e10),
  oneOff: num(0, 1e10),
  oneOffWeek: z.coerce.number().int().min(1).max(13),
  floor: num(0, 1e10),
});
export type CashInputs = z.infer<typeof cashInputSchema>;
export const cashDefaults: CashInputs = { cash: 180000, monthlyIn: 240000, monthlyOut: 270000, oneOff: 45000, oneOffWeek: 3, floor: 60000 };

export function cashWeeks(i: CashInputs) {
  const weeklyIn = (i.monthlyIn * 12) / 52;
  const weeklyOut = (i.monthlyOut * 12) / 52;
  const net = weeklyIn - weeklyOut;
  let closing = i.cash;
  const weeks = Array.from({ length: 13 }, (_, k) => {
    const week = k + 1;
    const out = weeklyOut + (week === i.oneOffWeek ? i.oneOff : 0);
    closing = closing + weeklyIn - out;
    return { week, in: weeklyIn, out, closing };
  });
  let lowest = weeks[0]!;
  for (const w of weeks) if (w.closing < lowest.closing) lowest = w;
  const firstNegative = weeks.find((w) => w.closing < 0)?.week ?? null;
  const firstUnderFloor = weeks.find((w) => w.closing < i.floor)?.week ?? null;
  const runwayWeeks = net < 0 ? Math.max(0, Math.floor((i.cash - i.oneOff) / -net)) : null;
  return { weeklyIn, weeklyOut, net, weeks, lowest, firstNegative, firstUnderFloor, runwayWeeks };
}

export function cashReport(i: CashInputs): ReportModel {
  const r = cashWeeks(i);
  const tone: Tone = r.lowest.closing < 0 ? "red" : r.lowest.closing < i.floor ? "amber" : "green";
  const verdict =
    tone === "red"
      ? `You run out of cash in week ${r.firstNegative}. This is a this week problem. Collections, payment terms and the price list are the three levers that move fastest.`
      : tone === "amber"
        ? `You stay above zero but drop under your floor in week ${r.firstUnderFloor}. Thirteen weeks of visibility is what stops this becoming a surprise.`
        : "You stay above your comfort floor for all 13 weeks. Keep the forecast running every week so it stays that way.";
  const actions =
    tone === "red"
      ? [
          "Ring your three largest overdue debtors this week and agree a payment date with each.",
          `Ask your two biggest suppliers for longer payment terms before week ${r.firstNegative}.`,
          "Lift prices on any work whose price has not moved in the last 12 months.",
        ]
      : tone === "amber"
        ? [
            `Rebuild this 13 week view every Friday so the week ${r.firstUnderFloor} dip never arrives as a surprise.`,
            "Ask whether the one off payment, or part of it, can move to a later week.",
            "Set a collections target for the next four weeks and give it to one named person.",
          ]
        : [
            "Keep a 13 week cash forecast running every Friday, even while cash is comfortable.",
            "Decide in advance what any surplus is for: debt, a cash buffer or investment.",
            "Report cash collected each week, not just sales, so the forecast stays honest.",
          ];
  return {
    slug: "cash-runway-calculator",
    toolName: "Cash Runway Calculator",
    headline: r.runwayWeeks !== null ? `${r.runwayWeeks} weeks of cash at today's burn, after the one off payment` : "Cash positive",
    stats: [
      { icon: "trend", label: "Net cash change per week", value: signedAud(r.net) },
      { icon: "low", label: "Lowest cash point in 13 weeks", value: aud(r.lowest.closing) },
      { icon: "calendar", label: "Week it lands", value: `Week ${r.lowest.week}` },
    ],
    tone,
    verdict,
    table: {
      caption: "13 week closing cash",
      headers: ["Week", "Cash in", "Cash out", "Closing cash"],
      rows: r.weeks.map((w) => [`Week ${w.week}`, aud(w.in), aud(w.out), aud(w.closing)]),
      highlight: [r.lowest.week - 1],
      negative: r.weeks.map((w, k) => (w.closing < 0 ? k : -1)).filter((k) => k >= 0),
    },
    actions,
    serviceName: "Rolling Cash Forecast",
    serviceText:
      "Ajility's Rolling Cash Forecast installs a 13 week cash forecast that your team rebuilds every week, so a dip is visible weeks before it lands and the decision is made while there is still time.",
    summary: { netPerWeek: Math.round(r.net), lowestCash: Math.round(r.lowest.closing), lowestWeek: r.lowest.week, runwayWeeks: r.runwayWeeks, tone },
  };
}

/* ------------------------------------------------------------------ */
/* Tool 2: Price change impact                                         */
/* ------------------------------------------------------------------ */
export const priceInputSchema = z.object({
  revenue: num(0, 1e11),
  margin: num(0.5, 95),
  price: num(-9, 9),
  volume: num(-100, 500),
});
export type PriceInputs = z.infer<typeof priceInputSchema>;
export const priceDefaults: PriceInputs = { revenue: 6000000, margin: 33, price: 3, volume: 0 };
export const PRICE_TABLE_MOVES = [-6, -3, 3, 6, 9];

export function priceMove(revenue: number, marginPct: number, pricePct: number, volumePct = 0) {
  const g = marginPct / 100;
  const p = pricePct / 100;
  const v = volumePct / 100;
  const gpNow = revenue * g;
  const sameVolume = revenue * p;
  const breakEven = g + p > 0 ? p / (g + p) : null;
  const newMargin = 1 + p !== 0 ? (g + p) / (1 + p) : 0;
  const withVolume = revenue * (1 + p) * (1 + v) * newMargin - gpNow;
  return { gpNow, sameVolume, breakEven, newMargin, withVolume };
}

export function priceReport(i: PriceInputs): ReportModel {
  const r = priceMove(i.revenue, i.margin, i.price, i.volume);
  const tone: Tone = i.price > 0 ? "green" : i.price < 0 ? "red" : "amber";
  const be = r.breakEven === null ? "an impossible" : pct(Math.abs(r.breakEven));
  const p = `${Math.abs(i.price)}`;
  const verdict =
    tone === "green"
      ? `At ${i.margin}% gross margin you could lose ${be} of your volume after a ${p}% price move and still make the same gross profit. Most owners fear a far bigger loss than they ever see.`
      : tone === "red"
        ? `A ${p}% discount needs ${be} more volume just to stand still. Discounting at ${i.margin}% margin is the most expensive thing on this page.`
        : "Standing still with rising costs is a price cut you did not choose.";
  const actions =
    tone === "green"
      ? [
          "Pick the three product lines with the oldest prices and set their new price this month.",
          "Write the price change letter to customers with a clear effective date.",
          `Track volume weekly for eight weeks after the change against the ${be} break even line.`,
        ]
      : tone === "red"
        ? [
            "Put any discount above a set level behind a named sign off.",
            "Measure the volume the last discount actually won before repeating it.",
            "Test a smaller price move on one customer group before changing the whole book.",
          ]
        : [
            "List the cost increases since your last price review.",
            "Model a 3% move in this calculator and share it with your leadership team.",
            "Set a date for the next price review and give it one owner.",
          ];
  const moves = [...new Set([...PRICE_TABLE_MOVES, i.price])].sort((a, b) => a - b);
  const rows = moves.map((m) => {
    const x = priceMove(i.revenue, i.margin, m);
    return [
      `${m > 0 ? "+" : ""}${m}%`,
      signedAud(x.sameVolume),
      x.breakEven === null ? "Not possible" : `${x.breakEven > 0 ? "-" : "+"}${pct(Math.abs(x.breakEven))}`,
      pct(x.newMargin),
    ];
  });
  return {
    slug: "price-change-impact-calculator",
    toolName: "Price Change Impact Calculator",
    headline: `${signedAud(r.sameVolume)} gross profit a year at the same volume`,
    headlineSub: r.gpNow > 0 ? `${r.sameVolume >= 0 ? "+" : "-"}${pct(Math.abs(r.sameVolume / r.gpNow))} of today's gross profit` : undefined,
    stats: [
      {
        icon: "percent",
        label: i.price >= 0 ? "Volume you could lose and still break even" : "Extra volume needed to break even",
        value: r.breakEven === null ? "Not possible" : pct(Math.abs(r.breakEven)),
      },
      { icon: "trend", label: "Gross margin after the change", value: pct(r.newMargin) },
      { icon: "check", label: "Gross profit change with your volume assumption", value: signedAud(r.withVolume) },
    ],
    tone,
    verdict,
    table: {
      caption: "Gross profit change by price move, same volume",
      headers: ["Price move", "Gross profit change", "Break even volume change", "New gross margin"],
      rows,
      highlight: [moves.indexOf(i.price)],
      negative: moves.map((m, k) => (m < 0 ? k : -1)).filter((k) => k >= 0),
    },
    actions,
    serviceName: "Pricing and Margin Review",
    serviceText:
      "Ajility's Pricing and Margin Review works through the price list, discount authority and margin by line, then sets the price moves and the rules that keep them in place.",
    summary: { price: i.price, sameVolumeGp: Math.round(r.sameVolume), breakEven: r.breakEven, newMargin: r.newMargin, tone },
  };
}

/* ------------------------------------------------------------------ */
/* Tool 3: Time and accountability grid                                */
/* ------------------------------------------------------------------ */
export const ENERGY = ["fuel", "neutral", "drain"] as const;
export const EDGE = ["expert", "competent", "weak"] as const;
export const ENERGY_LABEL: Record<(typeof ENERGY)[number], string> = { fuel: "Fuels me", neutral: "Neutral", drain: "Drains me" };
export const EDGE_LABEL: Record<(typeof EDGE)[number], string> = { expert: "Expert", competent: "Competent", weak: "Weak" };
export type Zone = "Keep" | "Grow" | "Hand Over" | "Let Go";
export const ZONES: Zone[] = ["Keep", "Grow", "Hand Over", "Let Go"];

const gridRow = z.object({
  id: z.string().max(40),
  name: z.string().trim().max(120),
  energy: z.enum(ENERGY),
  edge: z.enum(EDGE),
  hours: num(0, 100),
});
export const gridInputSchema = z.object({ rows: z.array(gridRow).max(40), ticked: z.array(z.string().max(40)).max(3) });
export type GridInputs = z.infer<typeof gridInputSchema>;
export type GridRow = z.infer<typeof gridRow>;

const seed: [string, GridRow["energy"], GridRow["edge"], number][] = [
  ["Quoting and pricing jobs", "fuel", "expert", 6],
  ["Chasing overdue invoices", "drain", "competent", 3],
  ["Weekly leadership meeting", "neutral", "competent", 3],
  ["Approving supplier bills", "drain", "weak", 3],
  ["Sales calls with key accounts", "fuel", "expert", 6],
  ["Fixing rostering clashes", "drain", "competent", 3],
  ["Reviewing the P&L", "neutral", "expert", 3],
  ["Payroll queries from staff", "drain", "weak", 3],
];
export const gridDefaults: GridInputs = {
  rows: seed.map(([name, energy, edge, hours], k) => ({ id: `r${k + 1}`, name, energy, edge, hours })),
  ticked: [],
};

export function zoneFor(energy: GridRow["energy"], edge: GridRow["edge"]): Zone {
  if (energy === "fuel") return edge === "expert" ? "Keep" : "Grow";
  if (energy === "neutral") return edge === "expert" ? "Keep" : edge === "competent" ? "Grow" : "Hand Over";
  return edge === "weak" ? "Let Go" : "Hand Over";
}

export function gridResult(i: GridInputs) {
  const rows = i.rows.filter((r) => r.name.trim().length > 0);
  const zones = Object.fromEntries(ZONES.map((z) => [z, { hours: 0, items: [] as GridRow[] }])) as Record<Zone, { hours: number; items: GridRow[] }>;
  for (const r of rows) {
    const z = zoneFor(r.energy, r.edge);
    zones[z].hours += r.hours;
    zones[z].items.push(r);
  }
  const total = rows.reduce((s, r) => s + r.hours, 0);
  const handOff = zones["Hand Over"].hours + zones["Let Go"].hours;
  return { rows, zones, total, handOff, share: total > 0 ? handOff / total : 0, yearly: handOff * 45 };
}

const hrs = (n: number) => `${Math.round(n * 10) / 10}`;

export function gridReport(i: GridInputs): ReportModel {
  const r = gridResult(i);
  const tone: Tone = r.handOff >= 18 ? "red" : r.handOff >= 9 ? "amber" : "green";
  const verdict =
    tone === "red"
      ? "More than two full days a week sit in Hand Over and Let Go. The business is paying leader rates for work a good coordinator or a system would do. That is the seat to design first."
      : tone === "amber"
        ? "More than a day a week is in the bottom two zones. Pick three items, name who takes each one, and set a date."
        : "Most of your week is in Keep and Grow. The question is whether the Keep work is the highest value use of a leader's time, or just the work you are best at.";
  const candidates = [...r.zones["Hand Over"].items, ...r.zones["Let Go"].items];
  const ticked = candidates.filter((c) => i.ticked.includes(c.id));
  const chosen = (ticked.length ? ticked : [...candidates].sort((a, b) => b.hours - a.hours)).slice(0, 3);
  const actions = chosen.map((c) => `Hand ${c.name.trim()} to a named person or a system by the end of the quarter.`);
  const fill = [
    "Book 30 minutes with your leadership team to agree who owns each item in Hand Over.",
    "Repeat this grid in a fortnight and compare the hours.",
    "Write down the one piece of Keep work only you can do, and protect time for it.",
  ];
  while (actions.length < 3) actions.push(fill[actions.length]!);
  return {
    slug: "time-and-accountability-grid",
    toolName: "Time and Accountability Grid",
    headline: `${hrs(r.handOff)} hrs a week on work someone else should own`,
    stats: [
      { icon: "clock", label: "Hours in Hand Over and Let Go", value: `${hrs(r.handOff)} hrs` },
      { icon: "percent", label: "Share of listed hours", value: pct(r.share, 0) },
      { icon: "calendar", label: "Hours a year (45 weeks)", value: `${Math.round(r.yearly)} hrs` },
    ],
    tone,
    verdict,
    table: {
      caption: "Your activities by zone",
      headers: ["Activity", "Energy", "Edge", "Hours a week", "Zone"],
      rows: r.rows.map((x) => [x.name, ENERGY_LABEL[x.energy], EDGE_LABEL[x.edge], hrs(x.hours), zoneFor(x.energy, x.edge)]),
      negative: r.rows.map((x, k) => (["Hand Over", "Let Go"].includes(zoneFor(x.energy, x.edge)) ? k : -1)).filter((k) => k >= 0),
    },
    actions,
    serviceName: "People, Culture and Accountability install",
    serviceText:
      "Ajility's People, Culture and Accountability install designs the seats, names one owner for each outcome and sets the decision rights, so work in Hand Over has somewhere to go.",
    summary: {
      handOffHours: r.handOff,
      totalHours: r.total,
      keep: r.zones.Keep.hours,
      grow: r.zones.Grow.hours,
      handOver: r.zones["Hand Over"].hours,
      letGo: r.zones["Let Go"].hours,
      tone,
    },
  };
}

/* ------------------------------------------------------------------ */
/* Tool 4: Fractional vs full time COO cost                            */
/* ------------------------------------------------------------------ */
export const cooInputSchema = z.object({
  salary: num(0, 1e8),
  superPct: num(0, 50),
  onCostPct: num(0, 100),
  recruitPct: num(0, 100),
  rampMonths: num(0, 24),
  retainer: z.union([num(0, 1e8), z.null()]),
  months: num(0, 12),
});
export type CooInputs = z.infer<typeof cooInputSchema>;
export const cooDefaults: CooInputs = { salary: 270000, superPct: 12, onCostPct: 18, recruitPct: 18, rampMonths: 6, retainer: null, months: 9 };

export function cooResult(i: CooInputs) {
  const sup = (i.salary * i.superPct) / 100;
  const on = (i.salary * i.onCostPct) / 100;
  const fee = (i.salary * i.recruitPct) / 100;
  const fullTime = i.salary + sup + on + fee;
  const fractional = i.retainer !== null && i.retainer > 0 ? i.retainer * i.months : null;
  const emptyWeeks = Math.round((i.rampMonths * 52) / 12);
  const diff = fractional === null ? null : fullTime - fractional;
  return { sup, on, fee, fullTime, fractional, emptyWeeks, diff };
}

const COO_TEST = "The real test is whether you need five days a week of COO, or the outcomes.";

export function cooReport(i: CooInputs): ReportModel {
  const r = cooResult(i);
  const cheaper = r.diff !== null && r.diff > 0;
  const tone: Tone = r.diff === null ? "amber" : cheaper ? "green" : "red";
  const verdict =
    r.diff === null
      ? `A full time COO costs about ${aud(r.fullTime)} in year one and the seat sits empty for about ${r.emptyWeeks} weeks while you recruit. ${COO_TEST}`
      : cheaper
        ? `On these figures the fractional arrangement costs ${aud(r.diff)} less in year one, and the seat is filled from the first month rather than after about ${r.emptyWeeks} weeks. ${COO_TEST}`
        : `On these figures the fractional arrangement costs ${aud(-r.diff!)} more in year one, while the full time hire leaves the seat empty for about ${r.emptyWeeks} weeks. ${COO_TEST}`;
  return {
    slug: "fractional-coo-cost-calculator",
    toolName: "Fractional COO vs Full Time COO Cost Calculator",
    headline:
      r.diff === null
        ? `${aud(r.fullTime)} full time all in, year one`
        : `${aud(Math.abs(r.diff))} ${cheaper ? "less" : "more"} in year one for the fractional arrangement`,
    headlineSub: r.diff === null ? "Enter a fractional retainer to compare." : undefined,
    stats: [
      { icon: "users", label: "Full time COO, year one all in", value: aud(r.fullTime) },
      { icon: "trend", label: "Fractional, year one", value: r.fractional === null ? "Not entered" : aud(r.fractional) },
      { icon: "calendar", label: "Weeks the seat sits empty", value: `${r.emptyWeeks} weeks` },
    ],
    tone,
    verdict,
    table: {
      caption: "Year one cost, line by line",
      headers: ["Item", "Year one"],
      rows: [
        ["Base salary", aud(i.salary)],
        [`Super (${i.superPct}%)`, aud(r.sup)],
        [`On costs (${i.onCostPct}%)`, aud(r.on)],
        [`Recruitment fee (${i.recruitPct}%)`, aud(r.fee)],
        ["Full time COO, year one", aud(r.fullTime)],
        [`Fractional retainer, ${i.months} months`, r.fractional === null ? "Not entered" : aud(r.fractional)],
      ],
      highlight: [4, 5],
    },
    actions: [
      "Write down the five outcomes the operating seat must deliver in the next 12 months.",
      "Estimate honestly how many days a week those outcomes need.",
      "Compare both options against the outcomes, not the hours, before you brief a recruiter.",
    ],
    serviceName: "Fractional COO retainer",
    serviceText:
      "An Ajility Fractional COO retainer puts a senior operator in the seat from the first month, on a rhythm sized to the outcomes rather than five days a week. Every fee is published on the packages page.",
    summary: { fullTime: Math.round(r.fullTime), fractional: r.fractional === null ? null : Math.round(r.fractional), emptyWeeks: r.emptyWeeks, tone },
  };
}

/* ------------------------------------------------------------------ */
/* Tool 5: Revenue leakage self check                                  */
/* ------------------------------------------------------------------ */
export const LEAK_QUESTIONS: { q: string; why: string; action: string }[] = [
  { q: "Is your price list more than 12 months old?", why: "Costs moved. Prices did not.", action: "Date every line on the price list and reprice the oldest ten this month." },
  { q: "Can sales staff give a discount without a sign off?", why: "Discretionary discounting is the biggest leak in most businesses this size.", action: "Set a discount limit for each role and require a named sign off above it." },
  { q: "Do credits get raised without a reason code?", why: "Credits without a reason cannot be managed.", action: "Add a required reason code to every credit and review the totals monthly." },
  { q: "Is there work delivered that is never invoiced, or invoiced late?", why: "Variations, extras, call outs and small jobs are the usual suspects.", action: "Match last month's completed jobs to invoices and invoice anything missing." },
  { q: "Do long standing customers sit on prices agreed years ago?", why: "Contract drift.", action: "List your top 20 customers by the date their pricing was last reviewed." },
  { q: "Are free extras such as delivery, support or rework given without being costed?", why: "Free is a price.", action: "Cost each free extra and decide which to charge for, keep or stop." },
  { q: "Do you offer settlement discounts that are taken even when paid late?", why: "A discount taken on a late payment is a straight loss.", action: "Reverse settlement discounts taken on late payments and tell customers the rule." },
  { q: "Are rebates or commissions paid without checking the trigger was met?", why: "A rebate or commission paid without checking the trigger is money given away.", action: "Check last quarter's rebates and commissions against their written triggers." },
  { q: "Would nobody know if the margin on a product line dropped this month?", why: "If margin by line is not reported monthly, leakage is invisible until year end.", action: "Start a monthly margin by product line report with one owner." },
];
export const leakInputSchema = z.object({ answers: z.array(z.boolean()).length(9) });
export type LeakInputs = z.infer<typeof leakInputSchema>;
export const leakDefaults: LeakInputs = { answers: Array(9).fill(false) as boolean[] };

export function leakReport(i: LeakInputs): ReportModel {
  const yes = i.answers.map((a, k) => (a ? k : -1)).filter((k) => k >= 0);
  const n = yes.length;
  const tone: Tone = n >= 6 ? "red" : n >= 3 ? "amber" : "green";
  const verdict =
    tone === "red"
      ? "Leakage is almost certainly above two percent of revenue. It needs a register, an owner and a monthly number."
      : tone === "amber"
        ? "Enough leaks to be worth a structured review. Start with the price list age and discount authority, they close fastest."
        : "Few obvious leaks. Keep margin by line reported monthly so any new leak shows up within weeks.";
  const actions = yes.slice(0, 3).map((k) => LEAK_QUESTIONS[k]!.action);
  const fill = [
    "Run this check again with your finance lead and compare answers.",
    "Pick one product line and check its margin month by month for the last year.",
    "Give one person ownership of pricing and discount rules.",
  ];
  for (const f of fill) if (actions.length < 3) actions.push(f);
  return {
    slug: "revenue-leakage-check",
    toolName: "Revenue Leakage Self Check",
    headline: `${n} of 9`,
    headlineSub: "likely leaks found",
    stats: [
      { icon: "alert", label: "Likely leaks", value: `${n}` },
      { icon: "check", label: "Areas clear", value: `${9 - n}` },
      { icon: "list", label: "Start with", value: n ? LEAK_QUESTIONS[yes[0]!]!.q.replace(/\?$/, "") : "Nothing urgent" },
    ],
    tone,
    verdict,
    table: {
      caption: "Your answers and why each yes leaks",
      headers: ["Question", "Answer", "Why it leaks"],
      rows: LEAK_QUESTIONS.map((x, k) => [x.q, i.answers[k] ? "Yes" : "No", i.answers[k] ? x.why : ""]),
      negative: yes,
    },
    actions,
    serviceName: "Pricing and Margin Review",
    serviceText:
      "Ajility's Pricing and Margin Review builds a leakage register, sets discount and credit rules with named owners, and puts margin by line in front of the leadership team every month.",
    summary: { score: n, yes: yes.map((k) => k + 1).join(","), tone },
  };
}

/* ------------------------------------------------------------------ */
/* Tool 6: Weekly scorecard builder                                    */
/* ------------------------------------------------------------------ */
export const MEASURE_BANK: { group: string; measures: string[] }[] = [
  { group: "Cash", measures: ["Cash in bank Friday close", "Debtors over 60 days", "Cash collected this week"] },
  { group: "Sales", measures: ["Quotes issued", "Quotes won", "Pipeline value", "New enquiries"] },
  { group: "Delivery", measures: ["Jobs completed on time %", "Rework or callbacks", "Hours billed vs hours worked %"] },
  { group: "Margin", measures: ["Gross margin this week %", "Discounts given", "Unbilled work"] },
  { group: "People", measures: ["Staff hours vs budget %", "Vacancies open", "Safety incidents"] },
];
export const SCORECARD_MIN = 3;
export const SCORECARD_MAX = 9;
const measure = z.object({ name: z.string().trim().min(1).max(80), group: z.string().max(30), owner: z.string().trim().max(60), target: z.string().trim().max(40) });
export const scoreInputSchema = z.object({ measures: z.array(measure).max(SCORECARD_MAX) });
export type ScoreInputs = z.infer<typeof scoreInputSchema>;
export const scoreDefaults: ScoreInputs = { measures: [] };

export function scoreReport(i: ScoreInputs): ReportModel {
  const n = i.measures.length;
  const owned = i.measures.filter((m) => m.owner.length > 0).length;
  const targeted = i.measures.filter((m) => m.target.length > 0).length;
  const complete = n >= SCORECARD_MIN && owned === n && targeted === n;
  const tone: Tone = complete ? "green" : "amber";
  const verdict = complete
    ? "Complete. Every measure has one owner and one weekly target."
    : n < SCORECARD_MIN
      ? `Pick at least ${SCORECARD_MIN} measures, and no more than ${SCORECARD_MAX}.`
      : `Nearly there. ${n - owned} measure${n - owned === 1 ? "" : "s"} still need an owner and ${n - targeted} still need a weekly target.`;
  return {
    slug: "weekly-scorecard-builder",
    toolName: "Weekly Scorecard Builder",
    headline: `${n} measures`,
    stats: [
      { icon: "list", label: "Measures chosen", value: `${n}` },
      { icon: "users", label: "With one owner", value: `${owned} of ${n}` },
      { icon: "check", label: "With a weekly target", value: `${targeted} of ${n}` },
    ],
    tone,
    verdict,
    table: {
      caption: "13 week scorecard",
      headers: ["Measure", "Owner", "Target", ...Array.from({ length: 13 }, (_, k) => `${k + 1}`)],
      rows: i.measures.map((m) => [m.name, m.owner, m.target, ...Array(13).fill("")]),
    },
    actions: [
      "Book a fixed 30 minute slot each week to review the scorecard with the owners named.",
      "Ask each owner to enter their number by Friday close, before the meeting.",
      "After four weeks, drop any measure nobody acted on and replace it.",
    ],
    serviceName: "Leadership Scorecard Build",
    serviceText:
      "Ajility's Leadership Scorecard Build sets the measures, owners and targets with your leadership team and runs the first weeks of the meeting, so the scorecard is still in use a quarter later.",
    summary: { measures: n, owned, targeted, tone },
  };
}

export const PARTNER_ROLES = ["Working partner", "Owner only (silent partner)", "Partly working, partly owner", "Investor shareholder"] as const;
export const PARTNER_AREAS = [
  { name: "Roles and contribution", icon: "Users", statements: ["Each partner's role and responsibilities are written down.", "We agree on who is a working partner and who is an owner only.", "The contribution each partner makes matches what we agreed when we started."], action: "Agree in writing what each partner is accountable for, and who is a working partner versus an owner only." },
  { name: "Reward", icon: "Coins", statements: ["How each partner is paid reflects the work they do, not just what they own.", "We have agreed how profit is shared once the roles are paid.", "I am comfortable with what each partner takes out of the business."], action: "Separate the reward for doing a role from the return on owning, then have your accountant set how each is paid." },
  { name: "Decisions and authority", icon: "Split", statements: ["We know which decisions one partner can make alone.", "We have an agreed way to break a deadlock.", "Big decisions get made on time, not put off."], action: "List the decisions that need every partner, the ones that do not, and how a deadlock is broken." },
  { name: "Money in and out", icon: "ArrowLeftRight", statements: ["Every loan or drawing between the partners and the company is recorded.", "Our accountant has reviewed money taken out of the company in the last year.", "We agree on how future funding or capital calls will be handled."], action: "Put every loan, drawing and funding commitment on one page and have your accountant review it, including Division 7A." },
  { name: "Protection", icon: "ShieldCheck", statements: ["We have a signed shareholders or partnership agreement that is up to date.", "We know what insurance is in place for directors and key people.", "Our agreement has been reviewed since the business last changed significantly."], action: "Check your shareholders agreement, directors and officers cover and key person arrangements with your lawyer and broker." },
  { name: "Exit and change", icon: "LogOut", statements: ["We have agreed what happens if a partner wants to leave.", "We have agreed what happens if a partner dies or becomes seriously ill.", "We have agreed how a departing partner's share would be valued and paid."], action: "Agree what happens on leaving, illness, death or deadlock, then have your lawyer reflect it in your agreement." },
] as const;
export const PARTNER_DISCLAIMER = "General information only. This check reflects one partner's view on the day it was completed. It is not legal, tax, financial product or insurance advice. Speak with your own lawyer, accountant and broker about your circumstances.";
export const partnerInputSchema = z.object({ role: z.enum(PARTNER_ROLES), answers: z.string().regex(/^[1-5]{18}$/) });
export type PartnerInputs = z.infer<typeof partnerInputSchema>;
export const partnerBand = (score: number) => score >= 4 ? "Aligned" : score >= 3 ? "Some gaps" : "Needs attention";
export function partnerResult(inputs: PartnerInputs) {
  const i = partnerInputSchema.parse(inputs);
  const areas = PARTNER_AREAS.map((a, k) => ({ ...a, score: [...i.answers.slice(k * 3, k * 3 + 3)].reduce((s, n) => s + Number(n), 0) / 3 }));
  const overall = areas.reduce((s, a) => s + a.score, 0) / 6;
  const sorted = [...areas].sort((a, b) => a.score - b.score);
  return { areas, sorted, overall, band: partnerBand(overall), lowest: sorted[0] ?? areas[0], underThree: areas.filter((a) => a.score < 3).length };
}
export function partnerReport(i: PartnerInputs): ReportModel {
  const r = partnerResult(i);
  const lowest = r.lowest;
  if (!lowest) throw new Error("Missing partnership areas");
  return {
    slug: "partner-alignment-check", toolName: "Partner Alignment Check",
    headline: `${r.overall.toFixed(1)} out of 5`, headlineSub: r.band,
    stats: [{ icon: "check", label: "Overall score", value: `${r.overall.toFixed(1)} out of 5` }, { icon: "low", label: "Lowest area", value: `${lowest.name}, ${lowest.score.toFixed(1)}` }, { icon: "alert", label: "Areas under 3", value: String(r.underThree) }],
    tone: r.overall >= 4 ? "green" : r.overall >= 3 ? "amber" : "red",
    verdict: r.overall >= 4 ? "Your partnership looks well aligned, from your side. This is your view only. Compare it with your partners to see whether they agree." : r.overall >= 3 ? "Some gaps worth talking about. Start with your lowest areas and compare your answers with your partners before deciding what to change." : "Important gaps to address. Start with your lowest areas and speak with your partners about what needs to be agreed in writing.",
    table: { caption: "Your partnership area scores", headers: ["Area", "Score out of 5", "Status"], rows: r.sorted.map((a) => [a.name, a.score.toFixed(1), partnerBand(a.score)]) },
    actions: r.sorted.slice(0, 3).map((a) => a.action),
    serviceName: "Partner Alignment Review",
    serviceText: "If the gaps are large, or your partners see things differently, the Partner Alignment Review works through all six areas with every partner and ends with a signed-off brief for your own lawyer and accountant.",
    disclaimer: PARTNER_DISCLAIMER,
    summary: { overall: r.overall, band: r.band, lowestArea: lowest.name, role: i.role, ...Object.fromEntries(r.areas.map((a) => [a.name, a.score])) },
  };
}
export type PartnerCodeData = { v: 1; n: string; r: PartnerInputs["role"]; a: string };
const partnerCodeSchema = z.object({ v: z.literal(1), n: z.string().trim().min(1).max(30), r: z.enum(PARTNER_ROLES), a: z.string().regex(/^[1-5]{18}$/) }).strict();
export function encodePartnerCode(name: string, inputs: PartnerInputs): string {
  const data = partnerCodeSchema.parse({ v: 1, n: name, r: inputs.role, a: inputs.answers });
  const bytes = new TextEncoder().encode(JSON.stringify(data));
  return "PAC1." + btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
export function decodePartnerCode(code: string): PartnerCodeData {
  if (!/^PAC1\.[A-Za-z0-9_-]+$/.test(code.trim()) || code.length > 1000) throw new Error("Invalid partner code");
  const body = code.trim().slice(5).replace(/-/g, "+").replace(/_/g, "/");
  const bytes = Uint8Array.from(atob(body), (c) => c.charCodeAt(0));
  return partnerCodeSchema.parse(JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes)));
}
export const partnerGapLabel = (gap: number) => gap >= 1 ? "talk first" : gap >= 0.5 ? "worth checking" : "";
export function partnerComparison(partners: PartnerCodeData[]) {
  if (partners.length < 2 || partners.length > 6) throw new Error("Compare two to six partners");
  const results = partners.map((p) => partnerResult({ role: p.r, answers: p.a }));
  return PARTNER_AREAS.map((a, k) => {
    const scores = results.map((r) => r.areas[k]?.score ?? 0);
    const min = Math.min(...scores), max = Math.max(...scores), gap = max - min;
    return { ...a, scores, min, max, gap, label: partnerGapLabel(gap) };
  });
}

/* ------------------------------------------------------------------ */
export const toolReportBuilders = {
  "cash-runway-calculator": (x: unknown) => cashReport(cashInputSchema.parse(x)),
  "price-change-impact-calculator": (x: unknown) => priceReport(priceInputSchema.parse(x)),
  "time-and-accountability-grid": (x: unknown) => gridReport(gridInputSchema.parse(x)),
  "fractional-coo-cost-calculator": (x: unknown) => cooReport(cooInputSchema.parse(x)),
  "revenue-leakage-check": (x: unknown) => leakReport(leakInputSchema.parse(x)),
  "weekly-scorecard-builder": (x: unknown) => scoreReport(scoreInputSchema.parse(x)),
  "partner-alignment-check": (x: unknown) => partnerReport(partnerInputSchema.parse(x)),
} satisfies Record<ToolSlug, (x: unknown) => ReportModel>;

export const TOOL_SLUGS = Object.keys(toolReportBuilders) as ToolSlug[];
